import type { LanguageCode } from '../types/i18n';

/**
 * Translation entry shape used across ServiceType / Provider profile / etc.
 */
interface TranslationEntry {
  languageCode: LanguageCode;
  name?: string | null;
  displayName?: string | null;
}

/**
 * Pick the best translation for the current user language.
 *
 * Order:
 *  1. Exact match for currentLanguage
 *  2. Match for tenant default language (if provided)
 *  3. First available translation
 *  4. undefined
 */
export function pickTranslation<T extends TranslationEntry>(
  translations: T[] | undefined | null,
  currentLanguage: LanguageCode,
  defaultLanguage?: LanguageCode,
): T | undefined {
  if (!translations || translations.length === 0) return undefined;
  const exact = translations.find((t) => t.languageCode === currentLanguage);
  if (exact) return exact;
  if (defaultLanguage) {
    const fb = translations.find((t) => t.languageCode === defaultLanguage);
    if (fb) return fb;
  }
  return translations[0];
}

/**
 * Resolve display name for an entity that has a `translations[]` array and a
 * fallback `name` string (default-language denormalized value).
 *
 * Picks the translation matching the user's interface language; falls back to
 * the entity's stored `name` (which is the tenant default language value).
 */
export function pickTranslatedName(
  entity: { name?: string | null; translations?: TranslationEntry[] } | null | undefined,
  currentLanguage: LanguageCode,
  defaultLanguage?: LanguageCode,
): string {
  if (!entity) return '';
  const translated = pickTranslation(entity.translations, currentLanguage, defaultLanguage);
  return (translated?.name ?? entity.name ?? '').trim();
}
